import {
  endOfDay,
  endOfWeek,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
} from "date-fns";

import type { HeadCount } from "../../types/api";
import type { ClientBusinessNode, CostCentreAttribute } from "../org/types";
import type {
  Budget,
  BudgetStatus,
  CostCentreMapping,
  PeriodCalculation,
} from "./types";

const getDepartmentsInDivision = (
  nodes: ReadonlyArray<ClientBusinessNode>,
  divisionId: string,
): ReadonlyArray<ClientBusinessNode> => {
  const byId = new Map(nodes.map((node) => [node.id, node] as const));

  return nodes.filter((child) => {
    if (child.type !== "department") return false;
    if (child.parentId === divisionId) return true;
    if (child.parentId === undefined) return false;

    const parent = byId.get(child.parentId);
    return parent?.type === "department" && parent.parentId === divisionId;
  });
};

export const mapToBudgetStatus = (
  hoursData: ReadonlyArray<Budget>,
  mappingsData: ReadonlyArray<CostCentreMapping>,
  plannedData: ReadonlyArray<HeadCount> = [],
): ReadonlyArray<BudgetStatus> => {
  // Hours API returns published rows only. A mapped CC with no hours row
  // means nothing is published — show the OrgList "Not published" heads-up.
  const budgetByCostCentre = new Map<string, number>();
  const costCentresWithPublishedRows = new Set<string>();

  hoursData.forEach((hour) => {
    costCentresWithPublishedRows.add(hour.costCentre);
    budgetByCostCentre.set(
      hour.costCentre,
      (budgetByCostCentre.get(hour.costCentre) ?? 0) + parseFloat(hour.budget),
    );
  });

  const plannedHoursByOrgId = new Map<string, number>();
  plannedData.forEach((plan) => {
    const plannedMinutes = plan.dayQuarters.reduce((sum, q) => sum + q, 0) * 15;
    plannedHoursByOrgId.set(plan.id, plannedMinutes / 60);
  });

  const budgetStatus: BudgetStatus[] = [];

  mappingsData.forEach((mapping) => {
    const planned = plannedHoursByOrgId.get(mapping.departmentId) ?? 0;
    // Hours rows key on costCentreCode (often the full id). Prefer
    // costCentreId, then the short CC label (e.g. CC6320) used in mappings.
    const budget =
      budgetByCostCentre.get(mapping.costCentreId) ??
      budgetByCostCentre.get(mapping.costCentre) ??
      0;
    const hasPublished =
      costCentresWithPublishedRows.has(mapping.costCentreId) ||
      costCentresWithPublishedRows.has(mapping.costCentre);

    budgetStatus.push({
      costCentre: mapping.costCentre,
      costCentreName: mapping.costCentreName,
      budget,
      planned,
      isOverBudget: planned > budget && budget > 0,
      isBudgetNotPublished: !hasPublished,
      unitName: mapping.unitName,
      divisionName: mapping.divisionName,
      departmentName: mapping.departmentName,
      subDepartmentName: mapping.subDepartmentName,
    });
  });

  return budgetStatus;
};

export const calculatePeriodHoursFromBudgetStatus = (
  hoursData: ReadonlyArray<Budget>,
  endDate: string,
  startDate: string,
): PeriodCalculation => {
  let minVal = 0;
  let maxVal = 0;
  const eodPeriodEnd = endOfDay(new Date(endDate));
  const sodPeriodStart = startOfDay(new Date(startDate));

  for (const item of hoursData) {
    const weekEnd = endOfWeek(new Date(item.startDate));

    if (
      (isBefore(weekEnd, eodPeriodEnd) || isEqual(weekEnd, eodPeriodEnd)) &&
      (isAfter(new Date(item.startDate), new Date(sodPeriodStart)) ||
        isEqual(new Date(item.startDate), new Date(sodPeriodStart)))
    ) {
      minVal += parseFloat(item.budget);
    }

    maxVal += parseFloat(item.budget);
  }

  return { min: minVal, max: maxVal };
};

const getChildrenForBudgetFilter = (
  nodes: ReadonlyArray<ClientBusinessNode>,
  parentId: string,
): ReadonlyArray<ClientBusinessNode> => {
  const parent = nodes.find((node) => node.id === parentId);
  if (parent?.type === "division") {
    return getDepartmentsInDivision(nodes, parentId);
  }

  return nodes.filter((node) => node.parentId === parentId);
};

export const filterNodesWithBudgetData = (
  nodes: ReadonlyArray<ClientBusinessNode>,
  budgetStatus: ReadonlyArray<BudgetStatus>,
  parentId: string,
): ReadonlyArray<ClientBusinessNode> => {
  const costCentresWithBudget = new Set(
    budgetStatus.map((status) => status.costCentre),
  );

  const children = getChildrenForBudgetFilter(nodes, parentId);

  return children.filter((node) => {
    if (node.type === "division") {
      const departmentChildren = getDepartmentsInDivision(nodes, node.id);

      return departmentChildren.some((dept) => {
        const costCentres =
          (dept.attributes?.["costCentre"] as CostCentreAttribute[]) ?? [];
        return costCentres.some((cc) => costCentresWithBudget.has(cc.publicId));
      });
    }

    if (node.type === "department") {
      const costCentres =
        (node.attributes?.["costCentre"] as CostCentreAttribute[]) ?? [];
      return costCentres.some((cc) => costCentresWithBudget.has(cc.publicId));
    }

    return true;
  });
};

export const filterNodesWithMappingData = (
  nodes: ReadonlyArray<ClientBusinessNode>,
  budgetStatus: ReadonlyArray<BudgetStatus>,
  parentId: string,
): ReadonlyArray<ClientBusinessNode> => {
  const allCostCentres = new Set(
    budgetStatus.map((status) => status.costCentre),
  );

  const children = getChildrenForBudgetFilter(nodes, parentId);

  return children.filter((node) => {
    if (node.type === "division") {
      const departmentChildren = getDepartmentsInDivision(nodes, node.id);

      return departmentChildren.some((dept) => {
        const costCentres =
          (dept.attributes?.["costCentre"] as CostCentreAttribute[]) ?? [];
        return costCentres.some((cc) => allCostCentres.has(cc.publicId));
      });
    }

    if (node.type === "department") {
      const costCentres =
        (node.attributes?.["costCentre"] as CostCentreAttribute[]) ?? [];
      return costCentres.some((cc) => allCostCentres.has(cc.publicId));
    }

    return true;
  });
};

export const filterBudgetDataForOrgNode = (
  hoursData: ReadonlyArray<Budget>,
  mappingsData: ReadonlyArray<CostCentreMapping>,
  selectedNode: ClientBusinessNode | undefined,
): {
  filteredHours: ReadonlyArray<Budget>;
  filteredMappings: ReadonlyArray<CostCentreMapping>;
} => {
  if (!selectedNode) {
    return { filteredHours: hoursData, filteredMappings: mappingsData };
  }

  if (selectedNode.type === "unit") {
    return { filteredHours: hoursData, filteredMappings: mappingsData };
  }

  if (selectedNode.type === "division") {
    const filteredMappings = mappingsData.filter(
      (mapping) => mapping.divisionId === selectedNode.publicId,
    );
    const divisionCostCentres = new Set(
      filteredMappings.map((mapping) => mapping.costCentreId),
    );
    const filteredHours = hoursData.filter((hour) =>
      divisionCostCentres.has(hour.costCentre),
    );
    return { filteredHours, filteredMappings };
  }

  if (selectedNode.type === "department") {
    const filteredMappings = mappingsData.filter(
      (mapping) => mapping.departmentId === selectedNode.publicId,
    );

    const departmentCostCentreIds = new Set(
      filteredMappings.map((mapping) => mapping.costCentreId),
    );

    const filteredHours = hoursData.filter((hour) =>
      departmentCostCentreIds.has(hour.costCentre),
    );

    return { filteredHours, filteredMappings };
  }

  return { filteredHours: hoursData, filteredMappings: mappingsData };
};

export const calculatePeriodBudgetFromFilteredData = (
  filteredHours: ReadonlyArray<Budget>,
  endDate: string,
  startDate: string,
): PeriodCalculation => {
  let minVal = 0;
  let maxVal = 0;
  const eodPeriodEnd = endOfDay(new Date(endDate));
  const sodPeriodStart = startOfDay(new Date(startDate));

  for (const item of filteredHours) {
    const weekEnd = endOfWeek(new Date(item.startDate));

    if (
      (isBefore(weekEnd, eodPeriodEnd) || isEqual(weekEnd, eodPeriodEnd)) &&
      (isAfter(new Date(item.startDate), new Date(sodPeriodStart)) ||
        isEqual(new Date(item.startDate), new Date(sodPeriodStart)))
    ) {
      minVal += parseFloat(item.budget);
    }

    maxVal += parseFloat(item.budget);
  }

  return { min: minVal, max: maxVal };
};
